import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';

const NAV_ITEMS = [
  { label: 'Home', to: '/' },
  { label: 'Series', to: '/series' },
  { label: 'News & Updates', to: '/news' },
  { label: 'About RDPT+', to: '/about' },
];

export const TopNav = ({ settings }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const title = settings?.title || 'RDPT+';
  const brand = settings?.brand_name || 'RDPT Plus';

  return (
    <header
      data-testid="top-nav"
      className="sticky top-0 z-50 border-b border-white/[0.06] backdrop-blur-md"
      style={{ background: 'rgba(8,4,4,0.85)' }}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="group flex items-center gap-3" data-testid="topnav-logo">
            <img
              src="/circle-rd-logo.png"
              alt="RDPT Plus"
              className="w-10 h-10 rounded-full object-contain transition-transform duration-300 group-hover:scale-105"
              style={{ filter: 'drop-shadow(0 2px 8px rgba(185,28,28,0.45))' }}
            />
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-heading text-base font-bold text-white tracking-tight">{title}</span>
              <span className="font-body text-[10px] tracking-[0.18em] uppercase text-white/40 mt-0.5">{brand}</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                data-testid={`topnav-link-${item.to.replace('/','') || 'home'}`}
                className={({ isActive }) =>
                  `relative px-3.5 py-2 rounded-md font-heading text-[12px] tracking-[0.18em] uppercase transition-colors ${
                    isActive ? 'text-primary-main' : 'text-white/60 hover:text-white'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    {isActive && (
                      <span
                        className="absolute left-1/2 -translate-x-1/2 bottom-0 w-6 h-[2px] rounded-full"
                        style={{ background: '#d4af37' }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Mobile toggle */}
          <button
            data-testid="topnav-mobile-toggle"
            className="lg:hidden w-10 h-10 rounded-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5"
            onClick={() => setOpen(!open)}
            aria-label="Toggle navigation"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-white/5" style={{ background: 'rgba(8,4,4,0.96)' }}>
          <nav className="max-w-[1400px] mx-auto px-4 py-3 flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                data-testid={`topnav-mobile-link-${item.to.replace('/','') || 'home'}`}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-md font-heading text-sm tracking-[0.15em] uppercase ${
                    isActive ? 'text-primary-main bg-white/[0.04]' : 'text-white/70 hover:bg-white/[0.03]'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};
